// @flow
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { i18n } from 'shared/utils';

type FieldDef = {
  key: string,
  label: string,
  required: boolean,
};

type Props = {
  headers: Array<string>,
  previewRows: Array<Object>,
  fieldDefinitions: Array<FieldDef>,
  mapping: Object,
  onMappingChange: (mapping: Object) => void,
};

const Wrapper = styled.div`
  margin: 16px 0;
`;

const MappingTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    border: 1px solid #e8e8e8;
    padding: 10px 12px;
    text-align: left;
  }
  th {
    background: #fafafa;
    font-weight: 600;
    font-size: 13px;
  }
  td {
    font-size: 13px;
  }
`;

const SelectField = styled.select`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  background: #fff;
  &:focus {
    border-color: #1890ff;
    outline: none;
  }
`;

const RequiredBadge = styled.span`
  background: #fff2f0;
  color: #f5222d;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 3px;
  margin-left: 6px;
  font-weight: 600;
`;

const MatchedBadge = styled.span`
  background: #f6ffed;
  color: #52c41a;
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 3px;
  margin-left: 6px;
  font-weight: 600;
`;

const PreviewSection = styled.div`
  margin-top: 24px;
  overflow-x: auto;
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th, td {
    border: 1px solid #e8e8e8;
    padding: 6px 10px;
    text-align: left;
    white-space: nowrap;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  th {
    background: #fafafa;
    font-weight: 600;
  }
  tr:nth-child(even) td {
    background: #fafafa;
  }
`;

const MappingSummary = styled.div`
  display: flex;
  gap: 16px;
  margin: 16px 0;
  font-size: 13px;
`;

const SummaryChip = styled.span`
  padding: 4px 10px;
  border-radius: 4px;
  font-weight: 500;
  background: ${p => p.bg || '#f0f0f0'};
  color: ${p => p.color || '#333'};
`;

class ColumnMapper extends PureComponent<Props> {
  handleSelect = (csvHeader: string, systemField: string) => {
    const { mapping, onMappingChange } = this.props;
    const newMapping = { ...mapping };

    if (systemField === '') {
      delete newMapping[csvHeader];
    } else {
      // Remove any other header mapped to this field
      for (const key of Object.keys(newMapping)) {
        if (newMapping[key] === systemField) {
          delete newMapping[key];
        }
      }
      newMapping[csvHeader] = systemField;
    }

    onMappingChange(newMapping);
  };

  getMappedCount = () => {
    return Object.keys(this.props.mapping).length;
  };

  getUnmappedRequired = () => {
    const { mapping, fieldDefinitions } = this.props;
    const mappedFields = new Set(Object.values(mapping));
    return fieldDefinitions.filter(f => f.required && !mappedFields.has(f.key));
  };

  getUsedFields = () => {
    return new Set(Object.values(this.props.mapping));
  };

  render() {
    const { headers, previewRows, fieldDefinitions, mapping } = this.props;
    const usedFields = this.getUsedFields();
    const unmappedRequired = this.getUnmappedRequired();

    return (
      <Wrapper>
        <MappingSummary>
          <SummaryChip bg="#e6f7ff" color="#1890ff">
            {this.getMappedCount()} / {headers.length} columns mapped
          </SummaryChip>
          {unmappedRequired.length > 0 && (
            <SummaryChip bg="#fff2f0" color="#f5222d">
              {unmappedRequired.length} required field(s) unmapped: {unmappedRequired.map(f => f.label).join(', ')}
            </SummaryChip>
          )}
          {unmappedRequired.length === 0 && (
            <SummaryChip bg="#f6ffed" color="#52c41a">
              All required fields mapped
            </SummaryChip>
          )}
        </MappingSummary>

        <MappingTable>
          <thead>
            <tr>
              <th style={{ width: '35%' }}>{i18n.t('CSV Column')}</th>
              <th style={{ width: '35%' }}>{i18n.t('Maps To')}</th>
              <th style={{ width: '30%' }}>{i18n.t('Sample Data')}</th>
            </tr>
          </thead>
          <tbody>
            {headers.map(header => {
              const currentField = mapping[header] || '';
              const sampleValues = previewRows
                .slice(0, 3)
                .map(row => row[header])
                .filter(Boolean)
                .join(', ');

              return (
                <tr key={header}>
                  <td>
                    <strong>{header}</strong>
                    {currentField && <MatchedBadge>Mapped</MatchedBadge>}
                  </td>
                  <td>
                    <SelectField
                      value={currentField}
                      onChange={e => this.handleSelect(header, e.target.value)}
                    >
                      <option value="">{i18n.t('— Skip this column —')}</option>
                      {fieldDefinitions.map(fd => (
                        <option
                          key={fd.key}
                          value={fd.key}
                          disabled={usedFields.has(fd.key) && currentField !== fd.key}
                        >
                          {fd.label}{fd.required ? ' *' : ''}
                        </option>
                      ))}
                    </SelectField>
                  </td>
                  <td style={{ color: '#666' }}>{sampleValues || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </MappingTable>

        {previewRows.length > 0 && (
          <PreviewSection>
            <h4 style={{ marginTop: 16 }}>
              {i18n.t(`Data Preview (first ${Math.min(previewRows.length, 5)} rows)`)}
            </h4>
            <PreviewTable>
              <thead>
                <tr>
                  {headers.map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.slice(0, 5).map((row, idx) => (
                  <tr key={idx}>
                    {headers.map(h => (
                      <td key={h}>{row[h] || ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </PreviewTable>
          </PreviewSection>
        )}
      </Wrapper>
    );
  }
}

export default ColumnMapper;
